import React, { useEffect, useState } from "react";
import {
	Alert,
	Box,
	Button,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	Divider,
	Grid,
	IconButton,
	TextField,
	Typography,
	useMediaQuery,
	useTheme,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { useForm } from "react-hook-form";
import { useApiMutation } from "src/hooks/useApiMutation";
import type {
	IB2BSession,
	IUpdateB2BSessionPayload,
} from "src/Types/module/b2bSession/b2bSession.types";

interface Props {
	open: boolean;
	handleClose: () => void;
	session: IB2BSession;
}

interface FormInputs {
	session_no: string;
	date: string;
	start_time: string;
	end_time: string;
}

const formatTimeToInput = (timeStr?: string) => {
	if (!timeStr) return "";
	return timeStr.slice(0, 5);
};

const UpdateB2BSessionModal: React.FC<Props> = ({
	open,
	handleClose,
	session,
}) => {
	const theme = useTheme();
	const fullScreen = useMediaQuery(theme.breakpoints.down("sm"));
	const [formError, setFormError] = useState<string | null>(null);

	const {
		register,
		handleSubmit,
		reset,
		watch,
		formState: { errors },
	} = useForm<FormInputs>({
		defaultValues: {
			session_no: session?.session_no || "",
			date: session?.date || "",
			start_time: formatTimeToInput(session?.start_time),
			end_time: formatTimeToInput(session?.end_time),
		},
	});

	useEffect(() => {
		if (session) {
			reset({
				session_no: session.session_no || "",
				date: session.date || "",
				start_time: formatTimeToInput(session.start_time),
				end_time: formatTimeToInput(session.end_time),
			});
			setFormError(null);
		}
	}, [session, reset]);

	const startTimeWatch = watch("start_time");
	const endTimeWatch = watch("end_time");

	// Compute session duration in minutes
	const calculateDuration = () => {
		if (!startTimeWatch || !endTimeWatch) return null;
		const [sHours, sMinutes] = startTimeWatch.split(":").map(Number);
		const [eHours, eMinutes] = endTimeWatch.split(":").map(Number);
		const startMinutesTotal = sHours * 60 + sMinutes;
		const endMinutesTotal = eHours * 60 + eMinutes;
		const diff = endMinutesTotal - startMinutesTotal;
		return diff > 0 ? diff : null;
	};

	const duration = calculateDuration();

	const { mutate: updateSession, isPending } = useApiMutation<
		IUpdateB2BSessionPayload,
		any
	>({
		method: "PATCH",
		endPoint: `/api/fair/admin/sessions/${session.id}`,
		invalidateQueries: ["B2BSession", "B2BSessions"],
	});

	const onSubmit = (data: FormInputs) => {
		setFormError(null);

		// Validate time range
		if (data.start_time >= data.end_time) {
			setFormError("End time must be later than start time.");
			return;
		}

		// Format to HH:mm:ss
		const formattedStartTime =
			data.start_time.length === 5 ? `${data.start_time}:00` : data.start_time;
		const formattedEndTime =
			data.end_time.length === 5 ? `${data.end_time}:00` : data.end_time;

		const payload: IUpdateB2BSessionPayload = {
			session_no: data.session_no.trim(),
			date: data.date,
			start_time: formattedStartTime,
			end_time: formattedEndTime,
		};

		updateSession(payload, {
			onSuccess: () => {
				handleModalClose();
			},
			onError: (err: any) => {
				console.error(err);
			},
		});
	};

	const handleModalClose = () => {
		setFormError(null);
		handleClose();
	};

	return (
		<Dialog
			open={open}
			onClose={handleModalClose}
			fullScreen={fullScreen}
			maxWidth="sm"
			fullWidth
		>
			<DialogTitle
				sx={{
					display: "flex",
					alignItems: "center",
					justifyContent: "space-between",
					px: 3,
					py: 2,
				}}
			>
				<Typography variant="h5" fontWeight={700}>
					Update Session
				</Typography>
				<IconButton onClick={handleModalClose} size="small">
					<CloseIcon fontSize="small" />
				</IconButton>
			</DialogTitle>

			<Divider />

			<Box component="form" onSubmit={handleSubmit(onSubmit)}>
				<DialogContent sx={{ p: 3 }}>
					{formError && (
						<Alert severity="error" sx={{ mb: 2 }}>
							{formError}
						</Alert>
					)}

					<Grid container spacing={2}>
						{/* Session Name */}
						<Grid item xs={12}>
							<TextField
								fullWidth
								label="Session Name"
								placeholder="Session Name"
								variant="outlined"
								size="small"
								InputLabelProps={{ shrink: true }}
								{...register("session_no", {
									required: "Session Name is required",
								})}
								error={!!errors.session_no}
								helperText={errors.session_no?.message}
							/>
						</Grid>

						{/* Session Date */}
						<Grid item xs={12}>
							<TextField
								fullWidth
								type="date"
								label="Session Date"
								size="small"
								InputLabelProps={{ shrink: true }}
								{...register("date", { required: "Date is required" })}
								error={!!errors.date}
								helperText={errors.date?.message}
							/>
						</Grid>

						{/* Start Time */}
						<Grid item xs={12} sm={6}>
							<TextField
								fullWidth
								type="time"
								label="Start Time"
								size="small"
								InputLabelProps={{ shrink: true }}
								inputProps={{ step: 300 }}
								{...register("start_time", {
									required: "Start time is required",
								})}
								error={!!errors.start_time}
								helperText={errors.start_time?.message}
							/>
						</Grid>

						{/* End Time */}
						<Grid item xs={12} sm={6}>
							<TextField
								fullWidth
								type="time"
								label="End Time"
								size="small"
								InputLabelProps={{ shrink: true }}
								inputProps={{ step: 300 }}
								{...register("end_time", {
									required: "End time is required",
								})}
								error={!!errors.end_time}
								helperText={errors.end_time?.message}
							/>
						</Grid>

						{/* Calculated Duration info */}
						{duration !== null && (
							<Grid item xs={12}>
								<Box
									sx={{
										p: 1.5,
										borderRadius: 1,
										bgcolor: "rgba(99, 102, 241, 0.08)",
										display: "flex",
										alignItems: "center",
										justifyContent: "space-between",
									}}
								>
									<Typography variant="body2" color="text.secondary">
										Session Duration:
									</Typography>
									<Typography variant="subtitle2" fontWeight={700} color="primary.main">
										{duration} Minutes
									</Typography>
								</Box>
							</Grid>
						)}
					</Grid>
				</DialogContent>

				<Divider />

				<DialogActions sx={{ px: 3, py: 2 }}>
					<Button variant="outlined" onClick={handleModalClose} disabled={isPending}>
						Cancel
					</Button>
					<Button type="submit" variant="contained" disabled={isPending}>
						{isPending ? "Saving..." : "Save Changes"}
					</Button>
				</DialogActions>
			</Box>
		</Dialog>
	);
};

export default UpdateB2BSessionModal;
